import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';
import {
	Button,
	CircleCheckmarkIcon,
	FlexGap,
	Modal,
	Paragraph,
	Popover,
	tokens,
} from '@treams/treams-ui';

const talentReviewVisibilityKey: { [key in graphql.TalentReviewVisibility]: string } = {
	[graphql.TalentReviewVisibility.Manager]: 'manager',
	[graphql.TalentReviewVisibility.Members]: 'everyone',
	[graphql.TalentReviewVisibility.Self]: 'self',
};

// Confirms submitting the grid for a cycle moment. Members and self are shared
// immediately; managers-only stays on the template and is not announced here.
// A resubmit lists only the people who will be mailed again.
export default function SubmitTalentReviewModal(props: {
	/** Title of the cycle moment the review is submitted for. */
	title: string;
	/** Template visibility that submit will share with. */
	visibility: graphql.TalentReviewVisibility;
	/** Group the review is shared with. */
	groupName: string;
	/**
	 * Null on a first submit. After a retract, the people who will be mailed;
	 * empty when nobody changed.
	 */
	recipients: { id: string; firstName: string; lastName: string }[] | null;
	loading?: boolean;
	onClose: () => void;
	onConfirm: () => void;
}) {
	const { t } = useTranslation();
	const sharesWithMembers = props.visibility !== graphql.TalentReviewVisibility.Manager;
	const recipients = [...(props.recipients ?? [])].sort(
		(a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
	);

	return (
		<Popover visible center onDismiss={props.onClose}>
			<Modal>
				<Modal.Header title={t('talentReviews.v2.cycle.submit')} onClose={props.onClose} />
				<Modal.Contents>
					<Paragraph>
						{t('talentReviews.v2.cycle.submitConfirmBody', { title: props.title })}
					</Paragraph>
					{sharesWithMembers && props.recipients === null && (
						<>
							<Paragraph mt={3}>
								{t('talentReviews.v2.cycle.submitShared', { group: props.groupName })}
							</Paragraph>
							<Paragraph mt={3}>
								{t(
									`talentReviews.v2.cycle.submitShared.visibility.${
										talentReviewVisibilityKey[props.visibility]
									}`,
								)}
							</Paragraph>
							<Paragraph mt={3}>{t('talentReviews.v2.cycle.submitShared.irreversible')}</Paragraph>
						</>
					)}
					{sharesWithMembers && props.recipients !== null && props.recipients.length === 0 && (
						<Paragraph mt={3}>{t('talentReviews.v2.cycle.submitReshare.unchanged')}</Paragraph>
					)}
					{sharesWithMembers && props.recipients !== null && props.recipients.length > 0 && (
						<>
							<Paragraph mt={3}>{t('talentReviews.v2.cycle.submitReshare')}</Paragraph>
							<FlexGap flexDirection="column" mt={3} mb={0}>
								<ul
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: tokens.gap.element,
										listStyle: 'none',
										margin: 0,
										padding: 0,
									}}
								>
									{recipients.map((user) => (
										<li key={user.id}>
											<Paragraph m={0}>{`• ${user.firstName} ${user.lastName}`}</Paragraph>
										</li>
									))}
								</ul>
							</FlexGap>
							<Paragraph mt={3}>{t('talentReviews.v2.cycle.submitReshare.email')}</Paragraph>
							<Paragraph mt={3}>
								{t(
									`talentReviews.v2.cycle.submitShared.visibility.${
										talentReviewVisibilityKey[props.visibility]
									}`,
								)}
							</Paragraph>
							<Paragraph mt={3}>{t('talentReviews.v2.cycle.submitShared.irreversible')}</Paragraph>
						</>
					)}
				</Modal.Contents>
				<Modal.Footer>
					<Button
						type="secondary"
						title={t('talentReviews.v2.cancel')}
						disabled={props.loading}
						onClick={props.onClose}
					/>
					<Button
						ml={2}
						type="primary"
						icon={<CircleCheckmarkIcon />}
						title={t('talentReviews.v2.cycle.submit')}
						loading={props.loading}
						onClick={props.onConfirm}
					/>
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
